/**
 * About / Contact / FAQ pages.
 * - [data-info-reveal]: glides in when scrolled into view (and sweeps the promise gauges).
 * - [data-journey]: the car drives along the "How it works" road as the section scrolls past.
 * - [data-faq]: live search with highlighting, topic filter chips, and smooth open/close answers.
 */
(function () {
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initReveal(root) {
    var items = root.querySelectorAll('[data-info-reveal]');
    if (!('IntersectionObserver' in window) || reduceMotion) {
      items.forEach(function (el) { el.classList.add('is-seen'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.remove('is-hidden');
        entry.target.classList.add('is-seen');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    items.forEach(function (el) {
      if (el.getBoundingClientRect().top > window.innerHeight * 0.92) el.classList.add('is-hidden');
      io.observe(el);
    });
  }

  function initJourney(root) {
    root.querySelectorAll('[data-journey]').forEach(function (section) {
      var road = section.querySelector('.about-journey__road');
      var steps = Array.prototype.slice.call(section.querySelectorAll('[data-journey-step]'));
      if (!road || !steps.length) return;
      if (reduceMotion) {
        road.style.setProperty('--journey', '100%');
        steps.forEach(function (s) { s.classList.add('is-reached'); });
        return;
      }
      var ticking = false;
      function update() {
        ticking = false;
        var rect = road.getBoundingClientRect();
        var vh = window.innerHeight;
        // 0 when the road enters the lower part of the screen, 1 once it has scrolled up past the middle.
        var p = (vh * 0.85 - rect.top) / (rect.height + vh * 0.35);
        p = Math.min(1, Math.max(0, p));
        road.style.setProperty('--journey', (p * 100).toFixed(1) + '%');
        steps.forEach(function (step, i) { step.classList.toggle('is-reached', p >= i / steps.length + 0.02); });
      }
      function onScroll() { if (!ticking) { ticking = true; window.requestAnimationFrame(update); } }
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      update();
    });
  }

  function escapeRegExp(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }

  function initFaq(root) {
    var faq = root.matches('[data-faq]') ? root : root.querySelector('[data-faq]');
    if (!faq) return;
    var input = faq.querySelector('[data-faq-search]');
    var count = faq.querySelector('[data-faq-count]');
    var empty = faq.querySelector('[data-faq-empty]');
    var groups = Array.prototype.slice.call(faq.querySelectorAll('[data-faq-group]'));
    var chips = Array.prototype.slice.call(faq.querySelectorAll('[data-faq-filter]'));
    var items = Array.prototype.slice.call(faq.querySelectorAll('[data-faq-item]'));
    var activeTopic = 'all';

    items.forEach(function (item) {
      var q = item.querySelector('.faq-item__q');
      item._q = q.textContent;
      item._text = (q.textContent + ' ' + item.querySelector('.faq-item__a').textContent).toLowerCase();
    });

    function apply() {
      var term = (input && input.value || '').trim().toLowerCase();
      var re = term ? new RegExp('(' + escapeRegExp(term) + ')', 'ig') : null;
      var shown = 0;
      groups.forEach(function (group) {
        var inTopic = activeTopic === 'all' || group.getAttribute('data-faq-group') === activeTopic;
        var groupShown = 0;
        group.querySelectorAll('[data-faq-item]').forEach(function (item) {
          var match = inTopic && (!term || item._text.indexOf(term) !== -1);
          item.hidden = !match;
          var q = item.querySelector('.faq-item__q');
          q.textContent = item._q;
          if (match && re) q.innerHTML = q.innerHTML.replace(re, '<mark>$1</mark>');
          // While searching, open the matches so the answer is visible; close them again when the search is cleared.
          if (term && match && !item.open) { item.open = true; item.dataset.autoOpened = 'true'; }
          if (!term && item.dataset.autoOpened) { item.open = false; delete item.dataset.autoOpened; }
          if (match) groupShown++;
        });
        group.hidden = groupShown === 0;
        group.classList.remove('is-hidden');
        shown += groupShown;
      });
      if (count) count.textContent = term ? shown + (shown === 1 ? ' result' : ' results') : '';
      if (empty) empty.hidden = shown !== 0;
    }

    if (input) {
      var timer;
      input.addEventListener('input', function () { window.clearTimeout(timer); timer = window.setTimeout(apply, 120); });
    }
    chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        activeTopic = chip.getAttribute('data-faq-filter');
        chips.forEach(function (c) { c.classList.toggle('is-active', c === chip); });
        apply();
        var body = faq.querySelector('.faq-body');
        if (body && body.getBoundingClientRect().top < 0) body.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      });
    });

    // Smooth open/close for the answers
    if (!reduceMotion && Element.prototype.animate) {
      items.forEach(function (item) {
        var summary = item.querySelector('summary');
        var answer = item.querySelector('.faq-item__a');
        summary.addEventListener('click', function (e) {
          e.preventDefault();
          if (item._anim) item._anim.cancel();
          delete item.dataset.autoOpened;
          if (item.open) {
            var h = answer.offsetHeight;
            item._anim = answer.animate([{ height: h + 'px', opacity: 1 }, { height: '0px', opacity: 0 }], { duration: 260, easing: 'cubic-bezier(.16,1,.3,1)' });
            item._anim.onfinish = function () { item.open = false; item._anim = null; };
          } else {
            item.open = true;
            var full = answer.offsetHeight;
            item._anim = answer.animate([{ height: '0px', opacity: 0 }, { height: full + 'px', opacity: 1 }], { duration: 360, easing: 'cubic-bezier(.16,1,.3,1)' });
            item._anim.onfinish = function () { item._anim = null; };
          }
        });
      });
    }
  }

  function init(root) {
    if (root.dataset.infoInit) return;
    root.dataset.infoInit = 'true';
    initReveal(root);
    initJourney(root);
    initFaq(root);
  }

  function initAll(scope) { (scope || document).querySelectorAll('[data-info-page]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initAll(); });
  else initAll();
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
